import { ROOM_TYPE_OPTIONS } from "../utils/constants";
import { useState } from "react";
import Button from "../../Button/Button";
import CheckIcon from "../../../assets/svg/check-icon";
import {
  selectCommunicationsFilters,
  selectCrmLeadsFilters,
  setCommunicationsRoomType,
  setCrmLeadsRoomType,
} from "../../../redux/slices/filterSlice";
import {
  setPmsRoomType,
  selectPmsPropertiesFilters,
} from "../../../redux/slices/pmsFilterSlice";
import { useDispatch, useSelector } from "react-redux";

interface CampaignStatusFilterProps {
  onClose: () => void;
  isCommunication?: boolean;
  isPms?: boolean;
  isMultiSelect?: boolean;
}

const RoomTypeFilter = ({
  onClose,
  isCommunication = false,
  isPms = false,
  isMultiSelect = true,
}: CampaignStatusFilterProps) => {
  const communicationsFilters = useSelector(selectCommunicationsFilters);
  const crmLeadsFilters = useSelector(selectCrmLeadsFilters);
  const pmsPropertiesFilters = useSelector(selectPmsPropertiesFilters);

  const roomType = isPms
    ? pmsPropertiesFilters?.roomType
    : isCommunication
    ? communicationsFilters?.roomType
    : crmLeadsFilters?.roomType;

  const dispatch = useDispatch();
  const [selectedRoomTypes, setSelectedRoomTypes] = useState<string[]>(
    Array.isArray(roomType.value)
      ? (roomType.value as string[])
      : roomType.value
      ? [roomType.value as string]
      : []
  );

  const handleSelectRoomType = (roomType: string) => {
    if (isMultiSelect) {
      setSelectedRoomTypes((prev) =>
        prev.includes(roomType)
          ? prev.filter((r) => r !== roomType)
          : [...prev, roomType]
      );
    } else {
      setSelectedRoomTypes((prev) =>
        prev.includes(roomType) ? [] : [roomType]
      );
    }
  };

  const handleApply = () => {
    const displayValues = selectedRoomTypes.map(
      (status) =>
        ROOM_TYPE_OPTIONS.find((option) => option.value === status)?.content ||
        status
    );

    const roomTypePayload = {
            value: selectedRoomTypes,
            displayValue: displayValues,
    };

    if (isPms) {
      dispatch(setPmsRoomType(roomTypePayload));
    } else if (isCommunication) {
      dispatch(setCommunicationsRoomType(roomTypePayload));
    } else {
      dispatch(setCrmLeadsRoomType(roomTypePayload));
    }
    onClose();
  };

  const isApplyDisabled = () => {
    return (
      JSON.stringify([...(selectedRoomTypes || [])].sort()) ===
      JSON.stringify([...((roomType?.value as string[]) || [])].sort())
    );
  };

  return (
    <div className="flex flex-col gap-2 mt-2 px-3">
      <span className="text-xxs font-semibold text-data dark:text-data-dk rounded-sm">
        SELECTED ({selectedRoomTypes?.length ?? 0})
      </span>
      {ROOM_TYPE_OPTIONS.map((option) => (
        <div
          key={option.value}
          className={`flex items-center justify-between p-3 text-md gap-2 cursor-pointer border border-border-gray dark:border-border-gray-dk rounded-lg text-heading-text dark:text-heading-text-dk ${
            selectedRoomTypes.includes(option.value)
              ? "bg-verba-blue-light-card-bg dark:bg-verba-blue-light-card-bg-dk border-verba-blue dark:border-verba-blue-dk"
              : ""
          }`}
          onClick={() => handleSelectRoomType(option.value)}
        >
          {option.content}
          <div
            className={`shrink-0 w-[14px] h-[14px] border border-gray-100 dark:border-gray-100-dk rounded-[3px] flex items-center justify-center ${
              selectedRoomTypes.includes(option.value)
                ? "bg-verba-blue text-pure-white dark:bg-verba-blue-dk dark:text-pure-white-dk border-verba-blue dark:border-verba-blue-dk"
                : ""
            }`}
          >
            {selectedRoomTypes.includes(option.value) && (
              <CheckIcon className="w-3 h-3 text-pure-white dark:text-pure-white-dk" />
            )}
          </div>
        </div>
      ))}
      <div className="flex justify-end gap-2 mt-4">
        <Button
          innerText="Cancel"
          buttonContainerClass="py-1 px-7.5 border border-verba-indigo dark:border-verba-indigo-dk rounded-lg"
          innerTextClass="text-md font-medium whitespace-nowrap text-heading-text dark:text-heading-text-dk"
          onClick={onClose}
        />
        <Button
          innerText="Apply"
          disabled={isApplyDisabled()}
          buttonContainerClass="py-1 px-7.5 rounded-lg bg-verba-indigo dark:bg-verba-indigo-dk disabled:opacity-50 disabled:cursor-not-allowed"
          innerTextClass="text-md font-medium whitespace-nowrap text-pure-white dark:text-pure-white-dk"
          onClick={handleApply}
        />
      </div>
    </div>
  );
};

export default RoomTypeFilter;
